import { ErrorIntegration } from "@features/tickets";
import { AlertCircle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { errorHandler } from "@/lib/utils/errorHandler";

/**
 * Query-specific error boundary for React Query errors
 * @param {Object} props - Component props
 * @param {Error} props.error - The error object
 * @param {Function} props.refetch - Function to retry the query
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.title] - Optional custom title
 */
export function QueryErrorBoundary({ error, refetch, className, title = "Erreur de chargement" }) {
  // Handle null or undefined error gracefully
  if (!error) {
    return null;
  }

  const userMessage = errorHandler.getUserMessage(error);

  // Log the query error
  errorHandler.logQueryError(error, {
    type: "Query Error",
    refetchAvailable: !!refetch,
  });

  return (
    <div className={cn("alert alert-error shadow-lg", className)}>
      <div className="flex items-start gap-3 w-full">
        <AlertCircle className="w-6 h-6 flex-shrink-0" strokeWidth={2} />

        <div className="flex-1">
          <h3 className="font-bold">{title}</h3>
          <p className="text-sm mt-1">{userMessage}</p>

          {/* Show technical details only in development */}
          {import.meta.env.DEV && error.response && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs opacity-70 hover:opacity-100">
                Détails techniques
              </summary>
              <pre className="mt-1 text-xs overflow-auto max-h-40 bg-base-100/20 p-2 rounded">
                {JSON.stringify(
                  {
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                  },
                  null,
                  2,
                )}
              </pre>
            </details>
          )}
        </div>

        {refetch && (
          <button onClick={refetch} className="btn btn-sm btn-ghost gap-2 flex-shrink-0">
            <RefreshCw className="w-4 h-4" />
            Réessayer
          </button>
        )}
      </div>

      <ErrorIntegration statusCode={error.response?.status} errorMessage={userMessage} />
    </div>
  );
}
